'use client';
import { Action, action, thunk, Thunk } from 'easy-peasy';
import http from '../../utils/httpClient';
import { toaster } from '@/utils/toast';
import { CURRENT_TOS_VERSION } from '@/utils/config';

export interface user {
  first_name: string;
  last_name: string;
  email: string;
  type: string;
  sso: boolean;
}

export interface profilePic {
  type: string;
  data: [];
}

export interface advisor {
  id: string;
  userId: string;
  company: string;
  crdNumber: string;
  linkedInUrl: string;
  firmAffiliation: string;
  phone: string;
  duties: string;
  crmValue: string;
  crmName: string;
  about: string;
  location: string;
  profilePic: profilePic;
  role: string;
  createdAt: string;
  tosAcceptedAt: string | null;
  tosAcceptedVersion: string | null;
}

export interface provider {
  id: string;
  userId: string;
  linkedInUrl: string;
  phone: string;
  startDate: string;
  brokerValue: true;
  brokerName: string;
  experience: string;
  hourType: string;
  skills: string;
  about: string;
  cv: string;
  role: string;
  profilePic: profilePic;
  location: string;
  cyberInsurancePolicy: boolean;
  cyberInsurancePolicyDoc: any;
  createdAt: string;
  tosAcceptedAt: string | null;
  tosAcceptedVersion: string | null;
}

export interface userObj {
  id: string;
  firstName: string;
  lastName: string;
  emailId: string;
  type: string;
  advisor: advisor | null;
  provider: provider | null;
  requestStatus: {
    id: string;
    userId: string;
    status: string;
    dateAdded: string;
  };
  emailVerified: boolean;
  tosAcceptedAt: string | null;
  tosAcceptedVersion: string | null;
}

export interface IAuthModel {
  /*****..........@...ANY TYPE...@.......*****/
  userCreate: user;
  userObj: userObj | undefined;

  /*****..........@...ACTION TYPE...@.......*****/
  setUserCreate: Action<IAuthModel, user>;
  setUserObj: Action<IAuthModel, userObj>;

  /*****..........@...THUNK TYPE...@.......*****/
  authCreate: Thunk<IAuthModel, user>;
  getUserObj: Thunk<IAuthModel>;
  acceptTos: Thunk<IAuthModel>;
}

const AuthModel: IAuthModel = {
  userCreate: {
    first_name: '',
    last_name: '',
    email: '',
    type: '',
    sso: false,
  },
  userObj: undefined,

  /*****..........@...ACTION TYPE...@.......*****/
  setUserCreate: action((state, payload) => {
    state.userCreate = payload;
  }),

  setUserObj: action((state, payload) => {
    state.userObj = payload;
  }),

  /*****..........@...THUNK TYPE...@.......*****/

  authCreate: thunk(async (actions, data) => {
    const response = await http()
      .post('/signup', data)
      .then((res) => {
        const { data } = res;
        actions.setUserCreate(data);
        toaster('success', 'User created successfully');
        return new Promise((resolve) => resolve('success'));
      })
      .catch((error) => {
        // Optional-chain through response/data: pre-fix the chain stopped
        // at `error?.` and then dereferenced `.response.data.error`
        // unconditionally -- a network error with no .response would
        // throw inside the catch and surface as an unhandled rejection.
        error?.response?.data?.error === 'This email is already in use'
          ? toaster('error', 'Email already exists!')
          : toaster('error', error?.error || 'Signup failed');
        // The Promise constructor's first arg is ALWAYS resolve regardless
        // of binding name -- the previous `(reject) => reject('error')` was
        // a misnomer that resolved with 'error' (callers branch on the
        // string value, not on .then/.catch). Renaming here matches the
        // actual runtime behaviour.
        return new Promise((resolve) => resolve('error'));
      });

    return response;
  }),

  getUserObj: thunk(async (action) => {
    if (localStorage.getItem('user')) {
      localStorage.removeItem('user');
    }
    const respo = await http()
      .get('/user')
      .then((res) => {
        action.setUserObj(res.data?.user);
        localStorage.setItem('user', '{"user":' + JSON.stringify(res.data?.user) + '}');
        return new Promise((resolve) => resolve('found'));
      })
      .catch((e) => {
        // 401 is handled globally by the response interceptor in
        // src/utils/httpClient.tsx (toast + redirect to /auth/login).
        if (e?.response?.statusText === 'Not Found') {
          // Resolves with 'Not found' (Promise constructor's first arg is
          // always resolve regardless of binding name). Callers branch on
          // the resolved string, not on rejection.
          return new Promise((resolve) => resolve('Not found'));
        }
      });
    return respo;
  }),

  acceptTos: thunk(async (actions) => {
    // Send the version the modal just displayed. The server validates
    // it against `tos_versions` and refuses unknown values; this guards
    // a stale tab from accepting a superseded version.
    const response = await http()
      .post('/user/accept-tos', { version: CURRENT_TOS_VERSION })
      .then(async () => {
        // Re-fetch the user record so userObj.tosAcceptedAt/Version
        // update and the re-prompt modal closes. getUserObj also
        // refreshes the localStorage cache so a subsequent reload
        // renders the gate correctly.
        await actions.getUserObj();
        return 'success';
      })
      .catch(() => {
        toaster('error', 'Could not record your acceptance. Please try again.');
        return 'error';
      });
    return response;
  }),
};

export default AuthModel;
